import type {
  AttachmentTemplateDto,
  MachineTemplateDto,
  QuotationDto,
  QuotationPriceOnlyDto,
  TradeInItemDto
} from '@api/machines'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'
import { updateMachinesAndAttachmentsPrices } from '@/pages/quotation-tool/utils/functions'
import { flattenMachineItems } from './deal-summary/calculations'
import { calculateQuotationItems } from './deal-summary/calculations/calculateQuotationItems'
import type { CombinedQuotationItems } from './deal-summary/types'

type QuoteStore = {
  // State
  isCustomerViewToggled: boolean
  tabValue: number
  isNonEditableQuote: boolean
  isQuoteAccepted: boolean
  selectedEquipments: AttachmentTemplateDto[] | MachineTemplateDto[]
  combinedQuotationItems: CombinedQuotationItems[] | null
  tradeInItems: TradeInItemDto[]
  skipQuoteSocket: boolean
  // Actions
  setIsCustomerViewToggled: (val: boolean) => void
  setTabValue: (val: number) => void
  setIsNonEditableQuote: (val: boolean) => void
  setIsQuoteAccepted: (val: boolean) => void
  setSelectedEquipments: (
    val: AttachmentTemplateDto[] | MachineTemplateDto[]
  ) => void
  setCombinedQuotationItems: (
    quote: QuotationDto,
    quoteNewPrices: QuotationPriceOnlyDto
  ) => void
  resetCombinedQuotationItems: () => void
  setSkipQuoteSocket: (flag: boolean) => void
}

const useQuoteStore = create<QuoteStore>()(
  devtools(set => ({
    // Initial state
    isCustomerViewToggled: false,
    tabValue: 0,
    isNonEditableQuote: false,
    isQuoteAccepted: false,
    selectedEquipments: [],
    combinedQuotationItems: null,
    tradeInItems: [],
    skipQuoteSocket: false,

    // Actions
    setIsCustomerViewToggled: (val: boolean) =>
      set({ isCustomerViewToggled: val }),

    setTabValue(val) {
      set({ tabValue: val })
    },

    setIsNonEditableQuote: (val: boolean) => set({ isNonEditableQuote: val }),

    setIsQuoteAccepted: (val: boolean) => set({ isQuoteAccepted: val }),

    setSelectedEquipments: (
      val: AttachmentTemplateDto[] | MachineTemplateDto[]
    ) => set({ selectedEquipments: val }),

    setCombinedQuotationItems: (
      quote: QuotationDto,
      quoteNewPrices: QuotationPriceOnlyDto
    ) =>
      set(() => {
        const { updatedMachinesList, updatedAttachmentsList } =
          updateMachinesAndAttachmentsPrices({
            oldMachinesList: quote.machineItems ?? [],
            oldAttachmentsList: quote.machineAttachmentItems ?? [],
            newMachinesList: quoteNewPrices?.machineItems ?? [],
            newAttachmentsList: quoteNewPrices?.machineAttachmentItems ?? []
          })

        const flattenedMachineItems = flattenMachineItems(updatedMachinesList)

        const { combinedQuotationItems: items } = calculateQuotationItems({
          machineAttachmentItems: updatedAttachmentsList,
          machineItems: flattenedMachineItems,
          recalculateSellPrice: !quote.isPriceLocked,
          tradeInItems: quote.tradeInItems ?? []
        })

        return {
          combinedQuotationItems: items,
          tradeInItems: quote.tradeInItems ?? []
        }
      }),

    resetCombinedQuotationItems: () => set({ combinedQuotationItems: null }),

    setSkipQuoteSocket: (flag: boolean) => set({ skipQuoteSocket: flag })
  }))
)

const selector = (state: QuoteStore) => state

export const useQuote = () => useQuoteStore(useShallow(selector))
